import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import { formatDate, formatDateLocal } from '../../utils/helper';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PatientVisitHistory = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [patients, setPatients] = useState([]);
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [startDate, endDate] = dateRange;
    const [filters, setFilters] = useState({
        start_date: formatDateLocal(new Date()),
        end_date: formatDateLocal(new Date()),
        patient_id: ''
    });

    const fetchReport = async () => {
        if (!filters.patient_id && !filters.start_date && !filters.end_date) {
            // Don't fetch if no filter is applied to avoid loading too much data initially
            return;
        }
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/reports/patient-visit-history',
                data: filters
            });
            if (response.data.status) {
                setData(response.data.data.appointments);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch patient visit history");
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            // Fetching all patients might be heavy, ideally use an autocomplete. 
            // For now fetching a reasonable limit or assuming a smaller dataset.
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/patients?limit=1000'
            });
            if (response.data.status) {
                setPatients(response.data.data.patients.data || response.data.data.patients);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    useEffect(() => {
        fetchReport();
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setFilters(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    };

    const exportToExcel = () => {
        const csvContent = "data:text/csv;charset=utf-8," 
            + "Appointment ID,Date,Patient,Doctor/Specialist,Type,Status\n"
            + data.map(e => {
                const doctorName = e.specialist ? `${e.specialist.name} (Specialist)` : (e.doctor?.name || 'N/A');
                return `${e.id},${formatDate(e.date)},${e.patient?.name || 'N/A'},${doctorName},${e.checkup_type?.title || 'N/A'},${e.status}`;
            }).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `patient_visit_history_${filters.start_date}_to_${filters.end_date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Patient Visit History
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1 gap-3 flex-wrap">
                                    <div className="d-flex align-items-center position-relative">
                                        <select
                                            name="patient_id"
                                            className="form-select form-select-solid w-250px"
                                            value={filters.patient_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Select Patient</option>
                                            {patients.map(patient => (
                                                <option key={patient.id} value={patient.id}>{patient.name} ({patient.phone})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="d-flex align-items-center position-relative">
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid w-250px"
                                            placeholderText="Select Date Range"
                                            dateFormat="yyyy-MM-dd"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-2">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Export to Excel</Tooltip>}>
                                        <button onClick={exportToExcel} className="btn btn-light-primary">
                                            <i className="fas fa-file-export me-2"></i> Export to Excel
                                        </button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            {loading ? <Loader /> : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th>Ref No</th>
                                                <th>Date</th>
                                                <th>Patient</th>
                                                <th>Doctor / Specialist</th>
                                                <th>Checkup Type</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {data.length > 0 ? (
                                                data.map((appt) => (
                                                    <tr key={appt.id}>
                                                        <td>{appt.ref_no}</td>
                                                        <td>{formatDate(appt.date)}</td>
                                                        <td>{appt.patient?.name}</td>
                                                        <td>
                                                            {appt.specialist ? (
                                                                <div className="d-flex flex-column">
                                                                    <span>{appt.specialist.name}</span>
                                                                    <span className="text-muted fs-8">Specialist</span>
                                                                </div>
                                                            ) : (
                                                                appt.doctor?.name
                                                            )}
                                                        </td>
                                                        <td>{appt.checkup_type?.name}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${appt.status === 'completed' ? 'success' : 'warning'}`}>
                                                                {appt.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>View Checkup Details</Tooltip>}>
                                                                <Link to={`/appointments/${appt.id}/checkup`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                                                                    <i className="fas fa-eye"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center">No records found. Select a patient or date range.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientVisitHistory;
